import { XMarkIcon } from "@";
import type { VerificationStage } from "../../types.ts";
import { ActionBar } from "./ActionBar.tsx";
import { MediaStage } from "./MediaStage.tsx";
import { useReviewConsole } from "./use-review-console.ts";
import { useReviewKeyboard } from "./use-review-keyboard.ts";

interface ReviewConsoleProps {
  visible: boolean;
  order: VerificationStage | null;
  initialAttachmentIndex: number;
  onClose: () => void;
  onAccept: (order: VerificationStage) => Promise<void>;
  onReject: (order: VerificationStage, comment: string) => Promise<void>;
  isSubmitting: boolean;
}

const ReviewConsole = ({
  visible,
  order,
  initialAttachmentIndex,
  onClose,
  onAccept,
  onReject,
  isSubmitting,
}: ReviewConsoleProps) => {
  const c = useReviewConsole({
    visible,
    order,
    initialAttachmentIndex,
    onAccept,
    onReject,
    isSubmitting,
  });

  useReviewKeyboard({
    visible,
    rejectMode: c.rejectMode,
    onClose,
    cancelRejectMode: c.cancelRejectMode,
    handleRejectConfirm: c.handleRejectConfirm,
    goPrevAttachment: c.goPrevAttachment,
    goNextAttachment: c.goNextAttachment,
    handleAccept: c.handleAccept,
    enterRejectMode: c.enterRejectMode,
  });

  if (!visible || !order) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-[1000] flex flex-col bg-black/80 backdrop-blur-md"
    >
      {/* Floating close button — no full header bar */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="إغلاق"
        className="absolute end-4 top-4 z-30 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white/80 ring-1 ring-white/20 backdrop-blur-md transition hover:bg-white/20 hover:text-white"
      >
        <XMarkIcon className="h-6 w-6" />
      </button>

      <div className="flex min-h-0 flex-1 flex-col px-4 py-4 md:px-6 md:py-5">
        <MediaStage
          attachments={c.attachments}
          attIdx={c.attIdx}
          setAttIdx={c.setAttIdx}
          goPrevAttachment={c.goPrevAttachment}
          goNextAttachment={c.goNextAttachment}
          setMediaLoading={c.setMediaLoading}
        />
      </div>

      {!c.noActions && (
        <ActionBar
          canAccept={c.canAccept}
          canReject={c.canReject}
          isSubmitting={isSubmitting}
          rejectMode={c.rejectMode}
          rejectComment={c.rejectComment}
          rejectError={c.rejectError}
          setRejectComment={c.setRejectComment}
          setRejectError={c.setRejectError}
          enterRejectMode={c.enterRejectMode}
          cancelRejectMode={c.cancelRejectMode}
          handleAccept={c.handleAccept}
          handleRejectConfirm={c.handleRejectConfirm}
          textareaRef={c.textareaRef}
        />
      )}
    </div>
  );
};

export default ReviewConsole;
